import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import GeometricBackground from '@/components/GeometricBackground';
import LegalLinks from '@/components/legal/LegalLinks';

const teacherTiers = [
  {
    name: 'Free',
    price: '€0',
    period: 'forever',
    description: 'Start teaching with core studio tools',
    features: [
      'Up to 5 students',
      'Bookings & calendar',
      'Blog & portfolio site',
      'Student invites',
      'Email support',
    ],
    highlighted: false,
    cta: 'Contact us to get started',
    ctaTo: '/contact',
  },
  {
    name: 'Pay as you go',
    price: '15%',
    period: 'platform fee on student payments',
    description: 'Full platform when you grow — no fixed monthly fee',
    features: [
      'Unlimited students',
      'Payments & lesson packages',
      'Meetings integration',
      'Analytics & AI practice (for your students)',
      'PAYG settled via Lemon Squeezy',
      'Email support',
    ],
    highlighted: true,
    cta: 'Contact us to get started',
    ctaTo: '/contact',
  },
] as const;

const PricingPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Pricing - Arrangelly</title>
        <meta
          name="description"
          content="Teachers: Free tier or pay-as-you-go. Students: teacher-set lesson subscriptions and AI practice purchases with 14-day refund if unused."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <GeometricBackground />
        <motion.div className="relative z-10 py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-violet-600 to-secondary bg-clip-text text-transparent">
                Pricing
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Teachers use pay-as-you-go billing. Students pay their teacher for lessons and optional AI
                features.
              </p>
              <p className="text-sm text-slate-500 mt-4 max-w-2xl mx-auto">
                <Link to="/how-it-works" className="text-violet-700 hover:underline font-medium">
                  How it works
                </Link>
                {' — '}
                invite-only access; we onboard teachers, teachers invite students.
              </p>
            </motion.div>

            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">For teachers &amp; schools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
              {teacherTiers.map((tier, index) => (
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative rounded-[1.75rem] p-8 border shadow-lg ${
                    tier.highlighted
                      ? 'bg-gradient-to-b from-violet-50 to-white border-primary border-2 ring-2 ring-primary/15'
                      : 'bg-white/80 backdrop-blur-sm border-slate-200/90'
                  }`}
                >
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{tier.name}</h3>
                    <p className="text-slate-500 text-sm mb-4">{tier.description}</p>
                    <span className="text-5xl font-bold text-slate-900">{tier.price}</span>
                    <p className="text-slate-500 text-sm mt-2">{tier.period}</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-slate-600">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    className={`w-full rounded-[1.75rem] ${
                      tier.highlighted
                        ? 'bg-gradient-to-r from-primary to-violet-600'
                        : 'bg-slate-800 hover:bg-slate-900'
                    } text-primary-foreground`}
                  >
                    <Link to={tier.ctaTo}>{tier.cta}</Link>
                  </Button>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mb-16 rounded-2xl border border-slate-200/90 bg-white/90 p-8 shadow-lg"
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-4">For students</h2>
              <ul className="space-y-4 text-slate-600 text-sm leading-relaxed">
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>Lesson subscriptions</strong> — Price and currency are set by your teacher (e.g.
                    monthly package of lessons). Billed as a subscription through our checkout (Lemon Squeezy).
                  </span>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>AI practice (one-time)</strong> — Optional add-on purchases for AI-assisted
                    practice feedback, where your teacher enables them.
                  </span>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>14-day refund</strong> — Full refund if you request it in the app within 14 days
                    and have not started using the paid service (no lessons booked/consumed, no AI credits
                    used). See our{' '}
                    <Link to="/refund-policy" className="text-violet-700 hover:underline font-medium">
                      Refund Policy
                    </Link>
                    .
                  </span>
                </li>
              </ul>
            </motion.div>

            <div className="text-center">
              <LegalLinks className="justify-center" />
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default PricingPage;
